import {
  WorkflowSplitBranchStatsView,
  WorkflowSplitGoal,
  WorkflowSplitStatsView,
  WorkflowSplitWinnerView,
} from "../../app/GeneratedApi";
import {
  getSplitGoalLabel,
  isEmailGoal,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/Split/split.helpers";

/** The backend's minimum per path before a winner can be called. */
export const SPLIT_MINIMUM_SAMPLE = 100;

export const SPLIT_STATS_FOOTNOTE =
  "Counts are contact entries — with re-entry on, a contact who enters twice counts twice. Email results count only emails that belong to one path before paths rejoin.";

export const CLEAR_WINNER_CONFIRMATION =
  "The split will go back to splitting contacts by percentage.";

export const getDeclareWinnerConfirmation = (branchName: string) =>
  `Every new contact reaching this split will go down ${branchName}. Contacts already past the split are not moved.`;

export type SplitStatsColumnFormat = "number" | "percent" | "currency";

export const SPLIT_STATS_COLUMNS: {
  key: keyof WorkflowSplitBranchStatsView;
  label: string;
  format: SplitStatsColumnFormat;
}[] = [
  { key: "percentage", label: "Split %", format: "percent" },
  { key: "entered", label: "Contacts entered", format: "number" },
  { key: "sent", label: "Sent", format: "number" },
  { key: "delivered", label: "Delivered", format: "number" },
  { key: "opens", label: "Opens", format: "number" },
  { key: "openRate", label: "Open rate", format: "percent" },
  { key: "clicks", label: "Clicks", format: "number" },
  { key: "clickRate", label: "Click rate", format: "percent" },
  { key: "conversions", label: "Conversions", format: "number" },
  { key: "conversionRate", label: "Conversion rate", format: "percent" },
  { key: "revenue", label: "Revenue", format: "currency" },
  {
    key: "revenuePerRecipient",
    label: "Revenue per recipient",
    format: "currency",
  },
];

const GOAL_COLUMNS: Record<
  WorkflowSplitGoal,
  keyof WorkflowSplitBranchStatsView
> = {
  OPEN_RATE: "openRate",
  CLICK_RATE: "clickRate",
  CONVERSION_RATE: "conversionRate",
  REVENUE_PER_RECIPIENT: "revenuePerRecipient",
};

/** Undefined when the split tracks no goal, so no column is highlighted. */
export const getGoalColumnKey = (goal: WorkflowSplitGoal | null) =>
  goal ? GOAL_COLUMNS[goal] : undefined;

const isLifetime = (stats: Pick<WorkflowSplitStatsView, "status">) =>
  stats.status === "LIFETIME";

export const getSplitStatusBadge = (
  stats: Pick<WorkflowSplitStatsView, "status" | "winner">
): { label: string; color: "info" | "warning" | "success" } => {
  if (isLifetime(stats)) {
    return { label: "Always splitting", color: "info" };
  }
  if (stats.status === "WINNER_DECLARED" && stats.winner) {
    return { label: `Winner: ${stats.winner.branchName}`, color: "success" };
  }
  if (stats.status === "INCONCLUSIVE") {
    return { label: "Inconclusive", color: "warning" };
  }
  return { label: "Testing", color: "info" };
};

export const getWinnerDecisionLabel = (winner: WorkflowSplitWinnerView) =>
  winner.decidedBy === "AUTOMATIC"
    ? "Decided automatically"
    : "Picked manually";

/** A date-time in the viewer's own timezone. */
export const formatSplitDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

export const getTestWindowLabel = (
  stats: Pick<
    WorkflowSplitStatsView,
    "winnerSelection" | "testStartedAt" | "testEndsAt"
  >,
  formatDateTime: (value: string) => string = formatSplitDateTime
) => {
  if (stats.winnerSelection !== "AUTOMATIC" || !stats.testStartedAt) {
    return null;
  }
  const end = stats.testEndsAt ? formatDateTime(stats.testEndsAt) : "…";
  return `Test window: ${formatDateTime(stats.testStartedAt)} → ${end}`;
};

export const getSignificanceLine = (
  stats: Pick<
    WorkflowSplitStatsView,
    | "status"
    | "goal"
    | "minimumSampleReached"
    | "significant"
    | "leaderBranchId"
    | "branches"
  >
) => {
  // Significance only matters for calling a winner: a lifetime split never
  // calls one, and with no goal there is nothing to compare.
  if (isLifetime(stats) || !stats.goal) {
    return null;
  }
  if (!stats.minimumSampleReached) {
    const unit = isEmailGoal(stats.goal) ? "deliveries" : "contacts";
    return `Not enough data yet — each path needs at least ${SPLIT_MINIMUM_SAMPLE} ${unit} before a winner can be called.`;
  }
  const leader = stats.branches.find(
    (branch) => branch.branchId === stats.leaderBranchId
  );
  if (!leader) {
    return null;
  }
  return stats.significant
    ? `${leader.name} is ahead with 95% confidence.`
    : `${leader.name} is ahead, but the difference is not statistically significant yet.`;
};

/**
 * The leader row's tag: "Leader" while testing, "Ahead on <goal>" for a
 * lifetime split (never "winner"), nothing without a goal.
 */
export const getLeaderLabel = (
  stats: Pick<WorkflowSplitStatsView, "status" | "goal">
) => {
  if (!stats.goal) return null;
  return isLifetime(stats)
    ? `Ahead on ${getSplitGoalLabel(stats.goal).toLowerCase()}`
    : "Leader";
};

/** A lifetime split refuses a winner (412), so it offers neither action. */
export const canDeclareWinner = (
  stats: Pick<WorkflowSplitStatsView, "status" | "winner">,
  branchId: string
) => !isLifetime(stats) && stats.winner?.branchId !== branchId;

export const canClearWinner = (
  stats: Pick<WorkflowSplitStatsView, "status" | "winner">
) => !isLifetime(stats) && !!stats.winner;
